import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '../../../lib/i18n';
import {
  heldSendRowOffersSendNow,
  isolateName,
  mergeTaskHeldSends,
  SEND_HOLD_LINGER_MS,
  sendHoldLiveState,
  type HeldSendRow as HeldSendRowData,
} from '../../../lib/send-hold';
import { useHeldSends } from '../../../lib/use-held-sends';
import { Typography } from '../ui';
import { HeldSendRow, useChannelLabel } from './HeldSendRow';
import styles from './HeldSendRow.module.css';

/**
 * A task's "Waiting to send": the messages this task wrote that the send rule is still holding, each with its
 * Cancel. Placed directly ABOVE "Messages sent" on the task drill-in (desktop and mobile), fed by
 * `/api/held-sends?taskId=`. A meeting's 2h hold pages out of the chat, so this list is its durable surface.
 *
 * - Hidden until the first successful read (there is nothing it could honestly show before), and when empty.
 * - A failed re-read KEEPS the rows and their Cancel (`mergeTaskHeldSends`, live state unknown); a row a complete
 *   re-read no longer lists stays as "No longer waiting" until its own clock hides it.
 * - The title leaves with the last row: one wake at the last row's hide time re-evaluates the section.
 * - A MEETING row (meeting hold) also offers Send now while the live row says so (`heldSendRowOffersSendNow`).
 *
 * Mirrored by iOS/Android `TaskHeldSendsSection`.
 */
export function TaskHeldSendsSection({ taskId }: { taskId: string }) {
  const { t } = useTranslation();
  const channelLabel = useChannelLabel();
  const { list, lastGood, refetch } = useHeldSends(taskId ? { taskId } : undefined);
  const shown = useRef<HeldSendRowData[]>([]);
  const rows = useMemo(() => {
    const merged = mergeTaskHeldSends(shown.current, lastGood);
    shown.current = merged;
    return merged;
  }, [lastGood]);
  const [now, setNow] = useState(() => Date.now());
  const visible = rows.filter((r) => Date.parse(r.sendAt) + SEND_HOLD_LINGER_MS > now);
  const lastHide = visible.reduce((at, r) => Math.max(at, Date.parse(r.sendAt) + SEND_HOLD_LINGER_MS), 0);
  useEffect(() => {
    if (!lastHide) return;
    const timer = setTimeout(() => setNow(Date.now()), Math.max(16, lastHide - Date.now()));
    return () => clearTimeout(timer);
  }, [lastHide]);

  if (lastGood === undefined || visible.length === 0) return null;
  return (
    <section className={styles.chips} aria-label={t('sendHold.waitingTitle')} data-testid="task-held-sends">
      <Typography variant="h4" color="primary">
        {t('sendHold.waitingTitle')}
      </Typography>
      {visible.map((row) => {
        const names = row.recipients.map((r) => r.displayName).filter((n): n is string => Boolean(n));
        return (
          <HeldSendRow
            key={row.heldSendId}
            holdId={row.heldSendId}
            fireAt={row.sendAt}
            bucket={row.bucket}
            title={t('sendHold.to', {
              name: isolateName(names.length > 0 ? names.join(', ') : t('tasks.sentOutreach.unnamedRecipient')),
              channel: channelLabel(row.recipients[0]?.channel),
            })}
            preview={row.textPreview}
            surface="task"
            live={sendHoldLiveState(row.heldSendId, list)}
            canCancel={row.canCancel}
            offersSendNow={heldSendRowOffersSendNow(row)}
            onZero={refetch}
            onSettled={refetch}
          />
        );
      })}
    </section>
  );
}
